import {DatePicker} from '@mui/lab';
import {
    Autocomplete,
    Button,
    ButtonProps,
    Checkbox,
    CheckboxProps,
    FormControlLabel,
    FormGroup,
    Grid,
    GridProps,
    IconButton,
    StandardTextFieldProps,
    TextField,
    Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {skipEnterAccept} from './AgGridAddons/utils';
import {PLFormRowInfo} from './PLForm';
import { forwardRef } from 'react';

interface PLGridItemProps extends GridProps {
    rowInfo: PLFormRowInfo;
    children: React.ReactElement;
}

//Simple wrapper for a grid item to compact the definition
export function PLGridItem(props: PLGridItemProps) {
    const {children, rowInfo, ...gridProps} = props;
    return (
        <Grid
            item
            xs={rowInfo.xs}
            sm={rowInfo.sm}
            md={rowInfo.md}
            lg={rowInfo.lg}
            xl={rowInfo.xl}
            {...gridProps}>
            {children}
        </Grid>
    );
}

interface PLTextFieldProps extends StandardTextFieldProps {
    lastRow?: boolean;
}

//Simple text entry field
export function PLTextField(props: PLTextFieldProps) {
    const {lastRow, ...fieldProps} = props;
    return (
        <TextField
            variant="outlined"
            onKeyDown={skipEnterAccept}
            fullWidth
            style={lastRow ? {paddingTop: '8px'} : {padding: '8px 0px 8px 0px'}}
            {...fieldProps}
        />
    );
}

interface PLCheckboxProps extends CheckboxProps {
    id: string;
    label: string;
    checked: boolean;
}

//Simple checkbox
export function PLCheckbox(props: PLCheckboxProps) {
    return (
        <FormGroup>
            <FormControlLabel
                control={<Checkbox {...props} />}
                label={props.label}
                name={props.name}
                onChange={props.onChange}
            />
        </FormGroup>
    );
}

interface PLButtonProps extends ButtonProps {
    onClick?(event: any): void;
    color?:
        | 'inherit'
        | 'primary'
        | 'secondary'
        | 'success'
        | 'error'
        | 'info'
        | 'warning';
    children: React.ReactNode;
    toolTip?: string;
}

//Simple button with tooltip
export function PLButton(props: PLButtonProps) {
    const {onClick, color, children, toolTip, ...ButtonProps} = props;

    return (
        <>
            {toolTip && (
                <Tooltip title={toolTip}>
                    <Button
                        color={color}
                        onClick={onClick}
                        children={children}
                        {...ButtonProps}></Button>
                </Tooltip>
            )}
            {!toolTip && (
                <Button
                    color={color}
                    onClick={onClick}
                    children={children}
                    {...ButtonProps}></Button>
            )}
        </>
    );
}

interface PLActiveButtonProps extends ButtonProps {
    active: boolean;
}

//Button for (in)activating a record - swaps based on status
export function PLActiveButton(props: PLActiveButtonProps) {
    const {active, ...buttonProps} = props;

    return props.active ? (
        <Button variant="contained" color="error" {...buttonProps}>
            Deactivate
        </Button>
    ) : (
        <Button variant="contained" color="success" {...buttonProps}>
            Activate
        </Button>
    );
}

export interface PLAutocompleteOption {
    id: string;
    label: string;
}

//Need to duplicate some intrinsic props, as extending the existing interface is...
// messy, to put it lightly
interface PLAutocompleteProps {
    id: string;
    key?: string;
    label?: string;
    options: PLAutocompleteOption[] | string[];
    autocomplete?: boolean;
    multiple?: boolean;
    getOptionLabel?: (option) => string;
    value: any;
    disabled?: boolean;
    disableClearable?: boolean;
    readOnly?: boolean;
    error?: boolean;
    helperText?: string;
    onChange?: (event, value) => void;
    onBlur?: (event) => void;
    onClick?: (event) => void;
    onKeyDown?: (event) => void;
    lastRow?: boolean;
    freeSolo?: boolean;
    autoFocus?: boolean;
    isOptionEqualToValue?: (option, value) => boolean;
    required?: boolean;
}

//Text field that resolves to pick-list options
export const PLAutocomplete = forwardRef((props: PLAutocompleteProps, ref)=> {
    return (
        <Autocomplete
            id={props.id}
            key={props.key}
            options={props.options}
            multiple={props.multiple}
            autoSelect={props.multiple || props.autocomplete}
            getOptionLabel={
                props.getOptionLabel
                    ? props.getOptionLabel
                    : (option) => option.label || ''
            }
            value={props.value}
            disabled={props.disabled}
            disableClearable={props.disableClearable}
            readOnly={props.readOnly}
            selectOnFocus
            openOnFocus
            fullWidth
            freeSolo={props.freeSolo}
            onChange={props.onChange}
            onBlur={props.onBlur}
            onKeyDown={skipEnterAccept}
            renderInput={(params) => (
                <TextField
                    inputRef={ref}
                    key={props.id}
                    name={props.id}
                    label={props.label}
                    variant="outlined"
                    onClick={props.onClick}
                    onKeyDown={props.onKeyDown}
                    error={props.error}
                    helperText={props.helperText}
                    style={
                        props.lastRow
                            ? {paddingTop: '8px'}
                            : {padding: '8px 0px 8px 0px'}
                    }
                    autoFocus={props.autoFocus}
                    required={props.required}
                    {...params}
                />
            )}
            isOptionEqualToValue={
                props.isOptionEqualToValue
                    ? props.isOptionEqualToValue
                    : (option, value) => option.id === value.id
            }
        />
    );
})

interface PLDatePickerProps {
    id: string;
    label?: string;
    value: Date | string;
    disabled?: boolean;
    disableClear?: boolean;
    error?: boolean;
    helperText?: string;
    onChange?: (value) => void;
    onBlur?: (event) => void;
    onKeyDown?: (event) => void;
    onAccept?: (value) => void;
    autoFocus?: boolean;
    lastRow?: boolean;
}

//Date Picker field
export function PLDatePicker(props: PLDatePickerProps) {
    const {lastRow, disableClear, ...otherProps} = props;

    return (
        <DatePicker
            {...otherProps}
            clearable={!disableClear}
            inputFormat="MM/DD/yyyy"
            value={props.value}
            disabled={props.disabled}
            onChange={props.onChange}
            onAccept={props.onAccept}
            renderInput={(params) => {
                return (
                    <TextField
                        {...params}
                        variant="outlined"
                        fullWidth
                        style={
                            lastRow
                                ? {
                                      paddingTop: '8px',
                                      zIndex: 2,
                                  }
                                : {
                                      padding: '8px 0px 8px 0px',
                                      zIndex: 2,
                                  }
                        }
                        onFocus={(event) => event.currentTarget.select()}
                        onBlur={props.onBlur}
                        onKeyDown={props.onKeyDown}
                        error={props.error}
                        helperText={props.helperText}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {props.value && !disableClear && (
                                        <IconButton
                                            edge="end"
                                            onClick={() => props.onChange(null)}
                                            style={{paddingRight: 8}}>
                                            <CloseIcon></CloseIcon>
                                        </IconButton>
                                    )}
                                    {params.InputProps?.endAdornment}
                                </>
                            ),
                        }}
                        autoFocus={props.autoFocus}
                    />
                );
            }}
        />
    );
}
